import { useEffect } from "react";

import { useLocalStorage } from "@common/hooks/filtering/useLocalStorage";
import { useSearchParams } from "react-router-dom";

export interface ActivityHistoryFilter {
  searchText: string;
  networkRequestTypes: {
    all: boolean;
    fetch: boolean;
    document: boolean;
    stylesheet: boolean;
    script: boolean;
    font: boolean;
    image: boolean;
    media: boolean;
    manifest: boolean;
    ws: boolean;
    wasm: boolean;
    other: boolean;
  };
  consoleLogs: boolean;
  systemEvents: boolean;
  userActions: boolean;
  screenViews: boolean;
  notificationEvents: boolean;
  customLogs: boolean;
  ai: boolean;
  errorsOnly: boolean;
}

const initialFilter: ActivityHistoryFilter = {
  searchText: "",
  networkRequestTypes: {
    all: true,
    fetch: false,
    document: false,
    stylesheet: false,
    script: false,
    font: false,
    image: false,
    media: false,
    manifest: false,
    ws: false,
    wasm: false,
    other: false,
  },
  consoleLogs: true,
  systemEvents: true,
  userActions: true,
  screenViews: true,
  notificationEvents: true,
  customLogs: true,
  ai: true,
  errorsOnly: false,
};

const activityHistoryFilterWebStorageKey = "shakebugs.activityHistoryFilterWeb";
const activityHistoryFilterMobileStorageKey = "shakebugs.activityHistoryFilterMobile";

export function useActivityHistoryFilter(isWebApp: boolean) {
  const { state: filter, setState: setFilter } = useLocalStorage<ActivityHistoryFilter>(
    isWebApp ? activityHistoryFilterWebStorageKey : activityHistoryFilterMobileStorageKey,
    initialFilter,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    try {
      const filterParam: string | null = searchParams.get("search");
      if (filterParam) {
        const decompressed: string = decodeURIComponent(window.atob(filterParam));
        const parsedFilter: ActivityHistoryFilter | null = JSON.parse(decompressed);
        if (!parsedFilter) return;

        if (JSON.stringify(parsedFilter) !== JSON.stringify(filter)) setFilter(parsedFilter);
      } else {
        const encodedFilter = window.btoa(encodeURIComponent(JSON.stringify(filter)));
        setSearchParams({ search: encodedFilter }, { replace: true });
      }
    } catch (error) {}
  }, [filter, searchParams, setFilter, setSearchParams]);

  const updateFilter = (filter: ActivityHistoryFilter) => {
    const encodedFilter = window.btoa(encodeURIComponent(JSON.stringify(filter)));
    setSearchParams({ search: encodedFilter }, { replace: true });
  };

  return {
    filter,
    updateFilter,
  };
}
